import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const COOKIE_CONSENT_KEY = 'cookie-consent';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState<Omit<CookiePreferences, 'timestamp'>>({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePrefs = (overridePrefs?: Omit<CookiePreferences, 'timestamp'>) => {
    const toSave = overridePrefs || prefs;
    const full: CookiePreferences = { ...toSave, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(full));
    setIsVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => savePrefs({ essential: true, analytics: true, marketing: true });

  return (
    <>
      <AnimatePresence>
        {isVisible && !showPrefs && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-3 left-3 z-50"
          >
            <div className="bg-card/95 backdrop-blur-sm border border-border rounded-md shadow-luxury px-3 py-2 flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Cookies.{' '}
                <a href="/cookie-policy" className="underline hover:text-foreground transition-colors">
                  Policy
                </a>
                {' · '}
                <button
                  onClick={() => setShowPrefs(true)}
                  className="underline hover:text-foreground transition-colors"
                >
                  Manage
                </button>
              </span>
              <Button
                size="sm"
                onClick={acceptAll}
                className="bg-accent hover:bg-accent/90 text-accent-foreground text-[10px] px-2 py-0.5 h-5"
              >
                OK
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="max-w-sm bg-card border-border shadow-luxury-lg p-0 overflow-hidden">
          {/* Header with gold accent line */}
          <div className="relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-gold" />
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle className="font-display text-xl tracking-tight text-foreground">
                Cookie Preferences
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Customize your privacy settings
              </p>
            </DialogHeader>
          </div>

          {/* Preference toggles */}
          <div className="px-6 pb-4 space-y-4">
            {/* Essential */}
            <div className="flex items-center justify-between py-3 border-b border-border/50">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Essential</p>
                <p className="text-xs text-muted-foreground">Required for site functionality</p>
              </div>
              <Switch 
                checked 
                disabled 
                className="data-[state=checked]:bg-accent opacity-60"
              />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between py-3 border-b border-border/50">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Analytics</p>
                <p className="text-xs text-muted-foreground">Help us improve your experience</p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                className="data-[state=checked]:bg-accent"
              />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between py-3">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Marketing</p>
                <p className="text-xs text-muted-foreground">Personalized ads & content</p>
              </div>
              <Switch
                checked={prefs.marketing}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                className="data-[state=checked]:bg-accent"
              />
            </div>
          </div>

          {/* Footer with buttons */}
          <DialogFooter className="px-6 py-4 bg-muted/30 border-t border-border/50 flex-row gap-3 sm:gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => savePrefs()}
              className="flex-1 border-border hover:bg-muted/50 hover:border-accent/50 transition-all"
            >
              Save Preferences
            </Button>
            <Button 
              size="sm" 
              onClick={acceptAll}
              className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground shadow-gold transition-all"
            >
              Accept All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Utility hook to check cookie consent
export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return consent;
};

export default CookieConsentBanner;
